// Falcon Console → Website Prices & Offers (the Falcon owner only, 01/11/2026): the yearly and monthly price of each
// package, the minimum users, offers (a percentage off or a new price, with an end date) and onboarding. Saving puts
// them on www.falconplm.com straight away; ended offers come off by themselves. New agreements are filled in from here.
import { badge, confirmBox, esc, formatDateTime, get, put, toast } from "../lib.js";
import { onAction, rerender, route } from "../state.js";
import { backBar, errorPanel, loadingPanel, renderShell, setContent } from "../shell.js";
import {
  BILLINGS, PRICE_PACKAGES, gbp, monthlyUplift, normalizePrices, offerLasts, offerStatus, priceProblems, shownOnboarding, shownPrice, ukDay,
  type Billing, type Offer, type PricePackage, type PriceSettings,
} from "../../../shared/websiteprices.js";

interface Loaded {
  prices: PriceSettings; updatedAt: string | null; saved: boolean; today: string;
  history: Array<{ by: string; action: string; changes: Array<{ field: string; from: string | null; to: string | null }>; at: string }>;
}
let L: Loaded | null = null;
/** The prices as on the screen now (not saved yet). */
let W: PriceSettings | null = null;
/** The offer being added or changed (a copy), and where it goes in the list (-1 = new). */
let editing: { at: number; offer: Offer } | null = null;
let dirty = false;

const MONTH_CHOICES = [0, 1, 2, 3, 6, 12, 18, 24, 36];
const lastsLabel = (m: number) => (m === 1 ? "First month" : m ? `First ${m} months` : "For as long as they stay");
const pkgShort = (k: string) => PRICE_PACKAGES.find((p) => p.key === k)?.short || k;
const money = (n: number | undefined | null) => (n === undefined || n === null || Number.isNaN(n) ? "" : String(n));
const STATUS: Record<string, [string, "green" | "blue" | "gray"]> = { live: ["Live now", "green"], scheduled: ["Starts", "blue"], ended: ["Ended", "gray"] };
const websiteLink = () => (location.hostname === "app.falconplm.com" ? "https://www.falconplm.com/#pricing" : "/website#pricing");

function pricesPanel(p: PriceSettings): string {
  const up = monthlyUplift(p);
  return `<section class="panel wp-box"><div class="panel-head"><div><h3>Package prices</h3><span class="muted">Per user per month, in pounds (GBP). No VAT.</span></div></div>
<div class="table-wrap"><table class="wp-prices"><thead><tr><th>Package</th><th>Billed yearly (£)</th><th>Billed monthly (£)</th><th>Label on the card</th></tr></thead><tbody>
${PRICE_PACKAGES.map((k) => { const x = p.packages[k.key]; const pct = Math.round((x.monthly / x.yearly - 1) * 1000) / 10; return `<tr><td><strong>${esc(k.label)}</strong></td>
<td><span class="wp-ph">Billed yearly (£)</span><input type="number" min="0.01" step="0.01" inputmode="decimal" data-wp="pkg.${k.key}.yearly" value="${money(x.yearly)}" aria-label="${esc(k.label)}, billed yearly"></td>
<td><span class="wp-ph">Billed monthly (£)</span><input type="number" min="0.01" step="0.01" inputmode="decimal" data-wp="pkg.${k.key}.monthly" value="${money(x.monthly)}" aria-label="${esc(k.label)}, billed monthly"><small class="muted wp-pct">${Number.isFinite(pct) && pct ? `${pct > 0 ? "+" : ""}${pct}%` : ""}</small></td>
<td><span class="wp-ph">Label on the card</span><input data-wp="pkg.${k.key}.label" value="${esc(x.label)}" maxlength="30" placeholder="None" aria-label="${esc(k.label)}, label"></td></tr>`; }).join("")}
</tbody></table></div>
<div class="wp-row"><div class="form-group"><label>Minimum users</label><input type="number" min="1" max="100" step="1" inputmode="numeric" data-wp="minUsers" value="${p.minUsers}"></div>
<button type="button" class="secondary small-btn" data-action="wp-uplift">Set monthly to yearly + 20%</button></div>
<p class="muted wp-note">The website works out the totals ("from ${esc(gbp(p.packages.plm.yearly * 12 * p.minUsers))} a year") and the "Save … per user" line from these prices.${up ? ` Monthly is now ${up}% more than yearly.` : ""}</p></section>`;
}

function offerRow(o: Offer, i: number, today: string): string {
  const st = offerStatus(o, today);
  const [label, colour] = STATUS[st]!;
  const when = st === "scheduled" ? `${label} ${ukDay(o.start)}` : st === "ended" ? `${label} ${ukDay(o.end)}` : label;
  const left = st === "live" ? Math.round((Date.parse(`${o.end}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86400000) + 1 : 0;
  const what = o.kind === "percent" ? `${o.percent}% off` : `New price ${o.packages.map((p) => o.billing.map((b) => `${pkgShort(p)} ${gbp(o.prices[p]?.[b] || 0)} ${b}`).join(" · ")).join(" · ")}`;
  return `<div class="wp-offer is-${st}"><div class="wp-offer-1"><strong>${esc(o.name)}</strong>${badge(when, colour)}<span class="muted wp-offer-end">${st === "live" ? `Ends ${ukDay(o.end)} · ${left === 1 ? "1 day left" : `${left} days left`}` : st === "scheduled" ? `Ends ${ukDay(o.end)}` : ""}</span></div>
<div class="wp-offer-2">${o.packages.map((p) => `<span class="wp-chip">${esc(pkgShort(p))}</span>`).join("")}<span>${esc(what)} · ${esc(o.billing.length === 2 ? "yearly and monthly" : `${o.billing[0]} only`)} · ${esc(offerLasts(o))}${o.banner ? " · banner" : ""}</span></div>
<div class="wp-offer-3">${st !== "ended" ? `<button type="button" class="secondary small-btn" data-action="wp-offer-edit" data-i="${i}">Edit</button>` : ""}${st === "live" ? `<button type="button" class="secondary small-btn" data-action="wp-offer-end" data-i="${i}">End now</button>` : `<button type="button" class="secondary small-btn" data-action="wp-offer-delete" data-i="${i}">Delete</button>`}</div></div>`;
}

function offersPanel(p: PriceSettings, today: string): string {
  const order = { live: 0, scheduled: 1, ended: 2 } as const;
  const list = p.offers.map((o, i) => ({ o, i })).sort((a, b) => order[offerStatus(a.o, today)] - order[offerStatus(b.o, today)] || a.o.start.localeCompare(b.o.start));
  return `<section class="panel wp-box"><div class="panel-head"><div><h3>Offers</h3><span class="muted">A percentage off, or a new price, until an end date. Ended offers come off the website by themselves.</span></div>
<button type="button" class="primary small-btn" data-action="wp-offer-add">+ Add an offer</button></div>
<div class="wp-offers">${list.length ? list.map(({ o, i }) => offerRow(o, i, today)).join("") : '<p class="muted">No offers yet.</p>'}</div></section>`;
}

function editorPanel(p: PriceSettings): string {
  if (!editing) return "";
  const o = editing.offer;
  const chk = (attr: string, value: string, on: boolean, label: string) => `<label class="check-row wp-check"><input type="checkbox" data-wp-o="${attr}" value="${value}" ${on ? "checked" : ""}> ${esc(label)}</label>`;
  const priceInputs = o.kind === "price" ? `<div class="form-group full"><label>Offer prices (£ per user per month)</label>
${o.packages.length && o.billing.length ? `<div class="table-wrap"><table class="wp-prices"><thead><tr><th>Package</th>${o.billing.map((b) => `<th>${esc(BILLINGS.find((x) => x.key === b)!.long)}</th>`).join("")}</tr></thead><tbody>
${o.packages.map((k) => `<tr><td><strong>${esc(pkgShort(k))}</strong></td>${o.billing.map((b) => `<td><span class="wp-ph">${esc(BILLINGS.find((x) => x.key === b)!.long)}</span><input type="number" min="0.01" step="0.01" inputmode="decimal" data-wp-o="price.${k}.${b}" value="${money(o.prices[k]?.[b])}" aria-label="${esc(pkgShort(k))}, ${b}"><small class="muted">Normal ${esc(gbp(p.packages[k][b]))}</small></td>`).join("")}</tr>`).join("")}
</tbody></table></div>` : '<small class="muted">Choose the packages and billing first.</small>'}</div>`
    : `<div class="form-group"><label>Percentage off</label><input type="number" min="1" max="90" step="0.5" inputmode="decimal" data-wp-o="percent" value="${money(o.percent)}"><small class="muted wp-example">${esc(percentExample(p, o))}</small></div>`;
  return `<section class="panel wp-box wp-edit" id="wp-editor"><div class="panel-head"><div><h3>${editing.at < 0 ? "New offer" : `Edit offer: ${esc(o.name)}`}</h3><span class="muted">One offer per package at a time.</span></div></div>
<div class="form-grid wp-form"><div class="form-message" role="alert" hidden></div>
<div class="form-group full"><label>Name shown on the website</label><input data-wp-o="name" value="${esc(o.name)}" maxlength="40" placeholder="For example Launch offer"></div>
<div class="form-group full"><label>Packages</label><div class="wp-checks">${PRICE_PACKAGES.map((k) => chk("packages", k.key, o.packages.includes(k.key), k.label)).join("")}</div></div>
<div class="form-group full"><label>Billing</label><div class="wp-checks">${BILLINGS.map((b) => chk("billing", b.key, o.billing.includes(b.key), b.label)).join("")}</div></div>
<div class="form-group full"><label>Type of offer</label><div class="wp-checks"><label class="check-row wp-check"><input type="radio" name="wp-kind" data-wp-o="kind" value="percent" ${o.kind === "percent" ? "checked" : ""}> Percentage off</label><label class="check-row wp-check"><input type="radio" name="wp-kind" data-wp-o="kind" value="price" ${o.kind === "price" ? "checked" : ""}> New price</label></div></div>
${priceInputs}
<div class="form-group"><label>Offer price lasts</label><select data-wp-o="months">${MONTH_CHOICES.concat(MONTH_CHOICES.includes(o.months) ? [] : [o.months]).map((m) => `<option value="${m}" ${m === o.months ? "selected" : ""}>${esc(lastsLabel(m))}</option>`).join("")}</select></div>
<div class="form-group"><label>Starts</label><input type="date" data-wp-o="start" value="${esc(o.start)}"></div>
<div class="form-group"><label>Ends</label><input type="date" data-wp-o="end" value="${esc(o.end)}"><small class="muted">Needed. The offer comes off the website after this day.</small></div>
<div class="form-group full"><label class="check-row"><input type="checkbox" data-wp-o="banner" ${o.banner ? "checked" : ""}> Show a banner at the top of the website</label></div>
<div class="form-group full form-actions"><button type="button" class="secondary" data-action="wp-offer-cancel">Cancel</button><button type="button" class="primary" data-action="wp-offer-save">Save offer</button></div>
</div></section>`;
}

function percentExample(p: PriceSettings, o: Offer): string {
  if (!(Number(o.percent) > 0) || !o.packages.length) return "";
  const b: Billing = o.billing[0] || "yearly";
  return o.packages.map((k) => `${pkgShort(k)} ${gbp(p.packages[k][b])} → ${gbp(Math.round(p.packages[k][b] * (1 - Number(o.percent) / 100) * 100) / 100)}`).join(" · ") + ` (${b})`;
}

function onboardingPanel(p: PriceSettings): string {
  const ob = p.onboarding;
  return `<section class="panel wp-box"><div class="panel-head"><div><h3>Onboarding</h3><span class="muted">Paid once. Optional for customers.</span></div></div>
<div class="form-grid wp-form">
<div class="form-group"><label>Normal price (£)</label><input type="number" min="0" step="1" inputmode="decimal" data-wp="ob.price" value="${money(ob.price)}"></div>
<div class="form-group"><label>Offer price (£)</label><input type="number" min="0" step="1" inputmode="decimal" data-wp="ob.offerPrice" value="${money(ob.offerPrice)}" placeholder="No offer"><small class="muted">Leave empty for no offer.</small></div>
<div class="form-group"><label>Offer name</label><input data-wp="ob.offerName" value="${esc(ob.offerName)}" maxlength="40"></div>
<div class="form-group"><label>Offer ends</label><input type="date" data-wp="ob.offerEnds" value="${esc(ob.offerEnds || "")}"><small class="muted">Optional.</small></div>
</div></section>`;
}

function previewPanel(p: PriceSettings, today: string): string {
  const card = (k: PricePackage) => {
    const y = shownPrice(p, k, "yearly", today), m = shownPrice(p, k, "monthly", today);
    return `<div class="wp-card">${y.offer ? `<span class="wp-ribbon">${esc(y.offer.name)} · ${esc(y.offer.what)}</span>` : ""}<strong>${esc(PRICE_PACKAGES.find((x) => x.key === k)!.label)}</strong>${p.packages[k].label ? ` <span class="wp-chip">${esc(p.packages[k].label)}</span>` : ""}
<div class="wp-card-price">${y.offer ? `<s>${esc(gbp(y.normal))}</s> ` : ""}${esc(gbp(y.price))} <small>/user/month</small></div>
${y.offer ? `<small class="wp-ends">${esc(y.offer.months ? `First ${y.offer.months} months` : "For as long as you stay")} · offer ends ${esc(ukDay(y.offer.end))}</small>` : ""}
<small class="muted">Billed monthly: ${m.offer ? `<s>${esc(gbp(m.normal))}</s> ` : ""}${esc(gbp(m.price))}</small></div>`;
  };
  const ob = shownOnboarding(p, today);
  return `<section class="panel wp-box"><div class="panel-head"><div><h3>How the website shows it</h3><span class="muted">Billed yearly, today${dirty ? " (not saved yet)" : ""}</span></div></div>
<div class="wp-cards">${PRICE_PACKAGES.map((k) => card(k.key)).join("")}
<p class="muted">Onboarding: ${ob.price < ob.normal ? `<s>${esc(gbp(ob.normal))}</s> ` : ""}${esc(gbp(ob.price))} one-off</p>
<a class="secondary small-btn" href="${esc(websiteLink())}" target="_blank" rel="noopener">Open the website ↗</a></div></section>`;
}

function usedPanel(): string {
  return `<section class="panel wp-box"><div class="panel-head"><div><h3>Where these prices are used</h3></div></div>
<ul class="wp-used"><li><strong>The website</strong>: price cards, the Pricing page and the offer banner.</li>
<li><strong>New agreements</strong> (Console → Agreements and Falcon HQ): the price is filled in from here, with the offer while it is live. You can still change it for one customer.</li>
<li><strong>Automatic card billing</strong>: when it is built, it will use these prices.</li>
<li>Customers who already signed keep their price. Changing a price here never changes what an organisation pays.</li></ul></section>`;
}

function historyPanel(): string {
  const h = L?.history || [];
  return `<section class="panel wp-box"><div class="panel-head"><div><h3>Changes</h3><span class="muted">Every change is also in the Activity log.</span></div></div>
${h.length ? `<ul class="wp-history">${h.map((x) => `<li><span class="muted">${esc(formatDateTime(x.at))} · ${esc(x.by)}</span><br>${x.changes.slice(0, 4).map((c) => `${esc(c.field)}: ${c.from ? `${esc(c.from)} → ` : ""}${esc(c.to || "removed")}`).join("<br>")}${x.changes.length > 4 ? `<br>and ${x.changes.length - 4} more` : ""}</li>`).join("")}</ul>` : '<p class="muted">No changes yet. The website shows the standard prices.</p>'}</section>`;
}

function draw(): void {
  if (!L || !W) return;
  const page = document.getElementById("wp-page");
  if (!page) return;
  page.innerHTML = `<div class="wp-grid"><div class="wp-col">${pricesPanel(W)}${offersPanel(W, L.today)}${editorPanel(W)}${onboardingPanel(W)}</div>
<div class="wp-col" id="wp-side">${previewPanel(W, L.today)}${usedPanel()}${historyPanel()}</div></div>
<div class="wp-savebar" ${dirty ? "" : "hidden"}><span>You have changes that aren't saved yet.</span><button type="button" class="secondary" data-action="wp-undo">Undo</button><button type="button" class="primary" data-action="wp-save">Save and update the website</button></div>`;
}
function drawSide(): void {
  const side = document.getElementById("wp-side");
  if (side && L && W) side.innerHTML = `${previewPanel(W, L.today)}${usedPanel()}${historyPanel()}`;
  const bar = document.querySelector<HTMLElement>(".wp-savebar");
  if (bar) bar.hidden = !dirty;
  const ex = document.querySelector<HTMLElement>(".wp-example");
  if (ex && W && editing) ex.textContent = percentExample(W, editing.offer);
}

route("/console/website-prices", async () => {
  renderShell({ active: "platformprices", title: "Website Prices & Offers", subtitle: "What www.falconplm.com shows. Saved changes are on the website straight away.", content: loadingPanel() });
  try {
    L = await get<Loaded>("/api/platform/website-prices");
    W = normalizePrices(JSON.parse(JSON.stringify(L.prices)));
    editing = null;
    dirty = false;
    setContent(`<div class="falcon-console-page wp-page">${backBar("/console", "Back to Overview")}<div id="wp-page"></div></div>`);
    draw();
    const root = document.getElementById("wp-page")!;
    root.addEventListener("input", (e) => onField(e.target as HTMLInputElement, false));
    root.addEventListener("change", (e) => onField(e.target as HTMLInputElement, true));
  } catch (err) {
    setContent(errorPanel((err as Error).message));
  }
});

const numOrNull = (v: string) => (v.trim() === "" ? null : Number(v));

function onField(el: HTMLInputElement, changed: boolean): void {
  if (!W) return;
  const k = el.dataset.wp;
  if (k) {
    const [a, b, c] = k.split(".");
    if (a === "pkg") {
      const x = W.packages[b as PricePackage];
      if (c === "label") x.label = el.value; else x[c as Billing] = Number(el.value);
    } else if (a === "minUsers") W.minUsers = Number(el.value);
    else if (a === "ob") {
      const ob = W.onboarding;
      if (b === "price") ob.price = Number(el.value);
      else if (b === "offerPrice") ob.offerPrice = numOrNull(el.value);
      else if (b === "offerName") ob.offerName = el.value;
      else if (b === "offerEnds") ob.offerEnds = el.value || null;
    }
    dirty = true;
    drawSide();
    return;
  }
  const o = el.dataset.wpO;
  if (!o || !editing) return;
  const off = editing.offer;
  if (o === "packages" || o === "billing") {
    const list = (off[o] as string[]).filter((v) => v !== el.value);
    if (el.checked) list.push(el.value);
    (off as any)[o] = o === "packages" ? PRICE_PACKAGES.map((p) => p.key).filter((p) => list.includes(p)) : BILLINGS.map((p) => p.key).filter((p) => list.includes(p));
  } else if (o === "kind") off.kind = el.value === "price" ? "price" : "percent";
  else if (o === "percent") off.percent = numOrNull(el.value);
  else if (o === "months") off.months = Number(el.value);
  else if (o === "banner") off.banner = el.checked;
  else if (o === "name" || o === "start" || o === "end") off[o] = el.value;
  else if (o.startsWith("price.")) {
    const [, p, b] = o.split(".");
    const v = numOrNull(el.value);
    const row = (off.prices[p as PricePackage] ||= {});
    if (v === null) delete row[b as Billing]; else row[b as Billing] = v;
  }
  // Redraw the editor when its shape changes (packages, billing, type); keep typing smooth otherwise.
  if (changed && (o === "packages" || o === "billing" || o === "kind")) {
    const box = document.getElementById("wp-editor");
    if (box) box.outerHTML = editorPanel(W);
  }
  const ex = document.querySelector<HTMLElement>(".wp-example");
  if (ex) ex.textContent = percentExample(W, off);
}

async function save(next: PriceSettings, done: string): Promise<boolean> {
  const problems = priceProblems(normalizePrices(next));
  if (problems.length) { showProblem(problems[0]!.message); return false; }
  try {
    const r = await put<{ ok: boolean; changed: number }>("/api/platform/website-prices", { prices: next });
    toast(r.changed ? done : "Nothing changed.");
    editing = null;
    await rerender();
    return true;
  } catch (err) {
    showProblem((err as Error).message);
    return false;
  }
}
function showProblem(message: string): void {
  const msg = document.querySelector<HTMLElement>("#wp-editor .form-message");
  if (msg) { msg.textContent = message; msg.hidden = false; msg.scrollIntoView({ block: "nearest" }); } else toast(message, "error");
}

onAction("wp-save", () => { if (W) void save(W, "Saved. The website shows the new prices now."); });
onAction("wp-undo", () => { if (L) { W = normalizePrices(JSON.parse(JSON.stringify(L.prices))); dirty = false; editing = null; draw(); } });
onAction("wp-uplift", () => {
  if (!W) return;
  for (const k of PRICE_PACKAGES) W.packages[k.key].monthly = Math.round(W.packages[k.key].yearly * 120) / 100;
  dirty = true;
  draw();
});
onAction("wp-offer-add", () => {
  if (!W || !L) return;
  const end = new Date(Date.parse(`${L.today}T00:00:00Z`) + 29 * 86400000).toISOString().slice(0, 10);
  editing = { at: -1, offer: { id: `offer-${Date.now().toString(36)}`, name: "", packages: ["plm", "both", "crm"], billing: ["yearly", "monthly"], kind: "percent", percent: 10, prices: {}, start: L.today, end, months: 0, banner: true } };
  draw();
  document.getElementById("wp-editor")?.scrollIntoView({ block: "start", behavior: "smooth" });
  document.querySelector<HTMLInputElement>('#wp-editor [data-wp-o="name"]')?.focus();
});
onAction("wp-offer-edit", (el) => {
  if (!W) return;
  const i = Number(el.dataset.i);
  editing = { at: i, offer: JSON.parse(JSON.stringify(W.offers[i])) };
  draw();
  document.getElementById("wp-editor")?.scrollIntoView({ block: "start", behavior: "smooth" });
});
onAction("wp-offer-cancel", () => { editing = null; draw(); });
onAction("wp-offer-save", () => {
  if (!W || !editing) return;
  const offers = W.offers.slice();
  if (editing.at < 0) offers.push(editing.offer); else offers[editing.at] = editing.offer;
  void save({ ...W, offers }, "Offer saved. The website shows it now.");
});
onAction("wp-offer-end", async (el) => {
  if (!W || !L) return;
  const i = Number(el.dataset.i);
  const o = W.offers[i]!;
  if (!(await confirmBox(`End "${o.name}" now? It comes off the website straight away. Agreements already sent keep their price.`, "End now"))) return;
  const yesterday = new Date(Date.parse(`${L.today}T00:00:00Z`) - 86400000).toISOString().slice(0, 10);
  const offers = W.offers.slice();
  offers[i] = { ...o, end: yesterday < o.start ? o.start : yesterday, start: yesterday < o.start ? yesterday : o.start };
  void save({ ...W, offers }, `"${o.name}" has ended.`);
});
onAction("wp-offer-delete", async (el) => {
  if (!W) return;
  const i = Number(el.dataset.i);
  const o = W.offers[i]!;
  if (!(await confirmBox(`Delete "${o.name}"? This can't be undone, but the change stays in the Activity log.`, "Delete", true))) return;
  void save({ ...W, offers: W.offers.filter((_, j) => j !== i) }, `"${o.name}" deleted.`);
});
